import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, RefreshCw, Users, Shield, Tag, Calendar, Database, Eye, Heart, Share2, Star } from 'lucide-react';
import { CardRenderer } from '../Cards/CardRenderer';
import { apiFetch } from '../../utils/api';

interface InfoUserLog {
    id: number;
    operation_id: string;
    target_username: string;
    card_id: number;
    owner_id: number;
    price: string;
    asset_type: string;
    listing_type: string;
    current_status: string;
    ratings: number;
    created_at: string;
    owner_username: string;
    first_name: string;
    last_name: string;
    photo_url: string;
    views: number;
    likes: number;
    shares: number;
}

export const InfoUsersPanel = () => {
    const [logs, setLogs] = useState<InfoUserLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // all, auction, market

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/admin/infousers?t=' + Date.now(), {
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });
            const data = await res.json();
            if (data.success) {
                setLogs(data.infousers || []);
            }
        } catch (error) {
            console.error('Failed to fetch infousers logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const filteredLogs = logs.filter(log => {
        const matchesSearch = log.operation_id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              log.target_username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (log.owner_username?.toLowerCase() || '').includes(searchTerm.toLowerCase());
                              
        const matchesFilter = filterType === 'all' || log.listing_type === filterType;
        
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-white flex items-center gap-2">
                        <Users className="text-blue-500" />
                        InfoUsers Tracking
                    </h2>
                    <p className="text-white/40 text-sm mt-1">Real-time monitoring of all added usernames (Auctions & Store)</p>
                </div>
                
                <button 
                    onClick={fetchLogs}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-white text-sm font-semibold transition-colors w-fit border border-white/10"
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin text-blue-400' : 'text-blue-400'} />
                    Refresh Logs
                </button>
            </div>

            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by OP-ID, Username, Owner..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                    />
                </div>
                <div className="flex gap-2">
                    {['all', 'auction', 'market'].map(type => (
                        <button
                            key={type}
                            onClick={() => setFilterType(type)}
                            className={`px-4 py-2 rounded-xl text-sm font-black uppercase tracking-wider transition-all border ${
                                filterType === type 
                                ? 'bg-blue-500 text-white border-blue-500' 
                                : 'bg-white/5 text-white/40 border-white/5 hover:bg-white/10 hover:text-white'
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="w-8 h-8 rounded-full border-r-2 border-t-2 border-blue-500 animate-spin"></div>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="text-center py-20 bg-white/5 rounded-3xl border border-white/5">
                    <Database className="mx-auto h-12 w-12 text-white/20 mb-4" />
                    <h3 className="text-lg font-bold text-white mb-1">No logs found</h3>
                    <p className="text-white/40 text-sm">No usernames have been tracked yet or none match your search.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    <AnimatePresence>
                        {filteredLogs.map((log) => (
                            <motion.div 
                                key={log.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="bg-black/40 border border-white/5 rounded-3xl p-5 hover:border-white/10 transition-colors"
                            >
                                <div className="flex flex-col lg:flex-row gap-6">
                                    {/* Left: Operation Info */}
                                    <div className="flex-1 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-xs font-mono font-bold tracking-widest">
                                                {log.operation_id}
                                            </div>
                                            <div className="text-xs text-white/40 font-mono flex items-center gap-1">
                                                <Calendar size={12} />
                                                {new Date(log.created_at).toLocaleString()}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-3xl font-black text-white/60 shadow-inner">
                                                @
                                            </div>
                                            <div>
                                                <div className="text-[10px] text-white/40 uppercase font-black tracking-widest mb-1">Target Username</div>
                                                <h3 className="text-2xl font-black text-white">{log.target_username}</h3>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-3">
                                            <div className="px-3 py-1.5 bg-white/5 rounded-xl border border-white/5 flex items-center gap-2">
                                                <Tag size={14} className="text-white/40" />
                                                <span className="text-sm font-bold text-white uppercase">{log.listing_type}</span>
                                            </div>
                                            <div className="px-3 py-1.5 bg-white/5 rounded-xl border border-white/5 flex items-center gap-2">
                                                <Shield size={14} className="text-white/40" />
                                                <span className="text-sm font-bold text-white uppercase">{log.asset_type}</span>
                                            </div>
                                            <div className="px-3 py-1.5 bg-green-500/10 rounded-xl border border-green-500/20 flex items-center gap-2 text-green-400">
                                                <span className="text-sm font-black">{parseFloat(log.price).toFixed(2)} GRAM</span>
                                            </div>
                                        </div>

                                        {/* Market Stats (Views, Likes, Shares, Ratings) */}
                                        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                                            <div className="flex items-center gap-1.5 text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded-lg">
                                                <Eye size={12} className="text-blue-400" />
                                                <span className="font-mono">{log.views || 0}</span> Views
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded-lg">
                                                <Heart size={12} className="text-red-400" />
                                                <span className="font-mono">{log.likes || 0}</span> Likes
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded-lg">
                                                <Share2 size={12} className="text-green-400" />
                                                <span className="font-mono">{log.shares || 0}</span> Shares
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded-lg">
                                                <Star size={12} className="text-yellow-400" />
                                                <span className="font-mono">{log.ratings || 0}</span> Rating
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Owner Info */}
                                    <div className="w-full lg:w-72 bg-white/[0.02] border border-white/5 rounded-2xl p-4 flex flex-col justify-center">
                                        <div className="text-[10px] text-white/40 uppercase font-black tracking-widest mb-3">Owner Details</div>
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src={log.photo_url || '/icons/default-avatar.png'} 
                                                className="w-12 h-12 rounded-full border border-white/10 bg-black"
                                                alt="Owner"
                                                onError={(e) => { e.currentTarget.src = '/icons/default-avatar.png'; }}
                                            />
                                            <div className="overflow-hidden">
                                                <div className="text-sm font-bold text-white truncate">
                                                    {log.first_name || ''} {log.last_name || ''}
                                                </div>
                                                <div className="text-xs text-blue-400 font-medium truncate">
                                                    @{log.owner_username || 'Unknown'}
                                                </div>
                                                <div className="text-[10px] text-white/30 font-mono mt-0.5">
                                                    ID: {log.owner_id}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Status Badge */}
                                        <div className={`mt-4 px-3 py-2 rounded-xl border text-center text-xs font-black uppercase tracking-widest ${
                                            log.current_status === 'sold' ? 'bg-purple-500/10 border-purple-500/20 text-purple-400' :
                                            log.current_status === 'auction' ? 'bg-orange-500/10 border-orange-500/20 text-orange-400' :
                                            'bg-blue-500/10 border-blue-500/20 text-blue-400'
                                        }`}>
                                            STATUS: {log.current_status}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};
